// Sopwith 3D - touch controls.
//
// On-screen buttons for phones and tablets: pitch on the left, throttle,
// gun and bomb on the right, autopilot at the top. Each button contributes
// command bits while a pointer is held on it; several fingers can hold
// several buttons. Shown only on touch devices (or with ?touch).

import {
  K_ACCEL,
  K_BOMB,
  K_DEACC,
  K_FLAPD,
  K_FLAPU,
  K_HOME,
  K_SHOT,
} from '../sim/constants';

interface Button {
  label: string;
  bits: number;
  cls: string;
}

const LEFT: Button[] = [
  { label: '▲', bits: K_FLAPU, cls: 'up' },
  { label: '▼', bits: K_FLAPD, cls: 'down' },
];

const RIGHT: Button[] = [
  { label: '+', bits: K_ACCEL, cls: 'accel' },
  { label: '−', bits: K_DEACC, cls: 'decel' },
  { label: 'FIRE', bits: K_SHOT, cls: 'fire' },
  { label: 'BOMB', bits: K_BOMB, cls: 'bomb' },
];

export function isTouchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0)
  );
}

export class TouchInput {
  private readonly root: HTMLElement;
  private readonly held = new Map<number, number>(); // pointerId -> bits
  private homeTap = false;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'touch';
    const left = this.cluster('touch-left', LEFT);
    const right = this.cluster('touch-right', RIGHT);
    const home = this.button({ label: 'HOME', bits: K_HOME, cls: 'home' });
    home.classList.add('touch-home');
    this.root.append(left, right, home);
    container.appendChild(this.root);
  }

  private cluster(cls: string, buttons: Button[]): HTMLElement {
    const box = document.createElement('div');
    box.className = `touch-cluster ${cls}`;
    for (const b of buttons) box.appendChild(this.button(b));
    return box;
  }

  private button(b: Button): HTMLElement {
    const el = document.createElement('div');
    el.className = `touch-btn ${b.cls}`;
    el.textContent = b.label;
    const down = (e: PointerEvent): void => {
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      this.held.set(e.pointerId, b.bits);
      el.classList.add('active');
      if (b.bits & K_HOME) this.homeTap = true;
    };
    const up = (e: PointerEvent): void => {
      this.held.delete(e.pointerId);
      el.classList.remove('active');
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('lostpointercapture', up);
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    return el;
  }

  setVisible(v: boolean): void {
    this.root.hidden = !v;
  }

  poll(): number {
    let keys = 0;
    for (const bits of this.held.values()) keys |= bits;
    if (this.homeTap) {
      keys |= K_HOME;
      this.homeTap = false;
    }
    return keys;
  }
}
